import { EditContractTerminationBody } from "@entity/data/dto/employee/contracts/termination/edit-contract-termination.body";
import { MedicalLeavePeriodMapper } from "@entity/data/mappers/employee/contracts/termination/medical-leave-period.mapper";
import { MedicalLeaveSubtypeEnumMapper } from "@entity/data/mappers/employee/medical-leave-subtype-enum.mapper";
import { TerminationCategoryEnumMapper } from "@entity/data/mappers/employee/termination-category-enum.mapper";
import { TerminationReasonEnumMapper } from "@entity/data/mappers/employee/termination-reason-enum.mapper";
import { EditContractTermination } from "@entity/domain/models/employee/contracts/termination/edit-contract-termination.model";
import { EditMedicalLeavePeriod } from "@entity/domain/models/employee/contracts/termination/medical-leave-period.model";
import { ContractTerminationFormValuesValidated } from "@entity/presentation/component/employee-form/contract-termination/form/contract-termination-form";
import { inject, injectable } from "inversify";

@injectable()
export class EditContractTerminationMapper {
    constructor(
        @inject(TerminationReasonEnumMapper)
        private readonly terminationReasonEnumMapper: TerminationReasonEnumMapper,
        @inject(TerminationCategoryEnumMapper)
        private readonly terminationCategoryEnumMapper: TerminationCategoryEnumMapper,
        @inject(MedicalLeaveSubtypeEnumMapper)
        private readonly medicalLeaveSubtypeEnumMapper: MedicalLeaveSubtypeEnumMapper,
        @inject(MedicalLeavePeriodMapper)
        private readonly medicalLeavePeriodMapper: MedicalLeavePeriodMapper,
    ) {}

    mapToDto(
        contractTermination: EditContractTermination,
    ): EditContractTerminationBody {
        const type = contractTermination.type
            ? this.terminationReasonEnumMapper.mapToDto(
                  contractTermination.type,
              )
            : null;

        const terminationCategory = this.terminationCategoryEnumMapper.reverse(
            contractTermination.terminationCategory,
        );

        const medicalLeaveSubtype = contractTermination.medicalLeaveSubtype
            ? this.medicalLeaveSubtypeEnumMapper.reverse(
                  contractTermination.medicalLeaveSubtype,
              )
            : undefined;

        const medicalLeavePeriods = contractTermination.medicalLeavePeriods
            ? contractTermination.medicalLeavePeriods.map((period) =>
                  this.medicalLeavePeriodMapper.reverse(period as any),
              )
            : undefined;

        return {
            id: contractTermination.id,
            termination_category: terminationCategory!,
            start_date: contractTermination.startDate.toISODate() ?? "",
            reason: contractTermination.reason,
            type: type,
            medical_leave_subtype: medicalLeaveSubtype,
            contract: contractTermination.contractId,
            cost: contractTermination.cost?.toString() ?? undefined,
            total_cost: contractTermination.totalCost?.toString() ?? undefined,
            end_date: contractTermination.endDate?.toISODate() ?? null,
            medical_leave_periods: medicalLeavePeriods as any,
        };
    }

    mapFromContractTerminationFormValues(
        contractTerminationId: number,
        contractTerminationForm: ContractTerminationFormValuesValidated,
    ): EditContractTermination {
        if (!contractTerminationId)
            throw Error("Missing income contractTerminationId");

        const medicalLeavePeriods =
            contractTerminationForm.medicalLeavePeriods?.map(
                (period) =>
                    new EditMedicalLeavePeriod(
                        period.id ?? 0,
                        contractTerminationId,
                        period.startDate!,
                        period.endDate!,
                        period.companySalaryPercentage!,
                        period.projectImputablePercentage!,
                        period.observations,
                    ),
            );

        const editContractTermination = new EditContractTermination(
            contractTerminationId,
            contractTerminationForm.terminationCategory,
            contractTerminationForm.startDate,
            contractTerminationForm.reason,
            contractTerminationForm.terminationReason ?? null,
            contractTerminationForm.contractId,
            contractTerminationForm.medicalLeaveSubtype,
            contractTerminationForm.cost,
            contractTerminationForm.totalCost,
            contractTerminationForm.endDate,
            medicalLeavePeriods as any,
        );

        return editContractTermination;
    }
}
